import React from 'react';
import {
  Button,
  Checkbox,
  Form,
  Header,
  Icon,
  Message,
} from 'semantic-ui-react';
import { modelSyncConfigV103, modelSyncTimeV103 } from './modelSyncConfigV103';

const statuses = [
  'idle',
  'disabled',
  'running',
  'success',
  'failed',
  'error',
  'partial',
  'skipped',
];
const messageCodes = [
  'removal_confirmation_pending',
  'free_metadata_unavailable',
  'manual_models_preserved',
  'catalog_unavailable',
  'catalog_partial',
  'catalog_invalid',
  'channel_changed',
  'alias_conflicts',
];

export default function ModelSyncV103({
  config,
  state,
  onChange,
  onSync,
  saved,
  disabled,
  syncDisabled,
  loading,
  needsRefresh,
  error,
  t,
}) {
  const label = (key) => t(`model_sync_v103.${key}`);
  const policy = modelSyncConfigV103(config);
  const missing = Object.entries(state?.missing_models || {}).filter(
    ([, count]) => Number(count) > 0
  );
  const action = label(needsRefresh ? 'refresh' : 'sync_now');
  const status = statuses.includes(state?.status)
    ? label(`statuses.${state.status}`)
    : label('unknown');
  return (
    <section aria-label={label('title')} style={{ marginBottom: 16 }}>
      <Header as='h4'>{label('title')}</Header>
      <Form.Select
        fluid
        selection
        label={label('provider')}
        name='model_sync_v103.provider'
        value={policy.provider || 'channel'}
        options={[
          { key: 'channel', value: 'channel', text: label('provider_legacy') },
          ...['openrouter', 'nvidia', 'zenmux'].map((value) => ({
            key: value,
            value,
            text: {
              openrouter: 'OpenRouter',
              nvidia: 'NVIDIA',
              zenmux: 'ZenMux',
            }[value],
          })),
        ]}
        disabled={disabled || loading}
        title={label('provider_help')}
        onChange={(event, { value }) =>
          onChange(event, {
            name: 'model_sync_v103',
            value: {
              ...config.model_sync_v103,
              ...policy,
              provider: value === 'channel' ? '' : value,
              add_free: value !== 'nvidia' && policy.add_free,
              add_all_new: policy.add_all_new,
              strip_free_suffix: policy.strip_free_suffix,
            },
          })
        }
      />
      {[
        'remove_unavailable',
        'remove_paid',
        'add_free',
        'add_all_new',
      ].map((name) => (
        <Form.Field key={name} title={label(`${name}_help`)}>
          <Checkbox
            toggle
            name={`model_sync_v103.${name}`}
            label={{
              children: (
                <span style={{ color: 'var(--ui-text-secondary)' }}>
                  {label(name)}
                </span>
              ),
            }}
            checked={policy[name]}
            disabled={
              disabled ||
              loading ||
              (name === 'add_free' && policy.provider === 'nvidia')
            }
            onChange={(event, { checked }) =>
              onChange(event, {
                name: 'model_sync_v103',
                value: {
                  ...config.model_sync_v103,
                  ...policy,
                  [name]: checked,
                  ...(name === 'add_all_new' && checked
                    ? { add_free: false }
                    : {}),
                  ...(name === 'add_free' && checked
                    ? { add_all_new: false }
                    : {}),
                },
              })
            }
          />
        </Form.Field>
      ))}
      <div
        role='status'
        aria-live='polite'
        style={{ overflowWrap: 'anywhere', marginBottom: 12 }}
      >
        <div>
          {label('status')}: {loading ? label('statuses.running') : status}
        </div>
        {['last_attempt_at', 'last_success_at', 'next_run_at'].map((name) => (
          <div key={name}>
            {label(name)}: {modelSyncTimeV103(state?.[name], label('unknown'))}
          </div>
        ))}
        {Number.isFinite(state?.added_count) && (
          <div>
            {label('added_count')}: {state.added_count}
          </div>
        )}
        {Number.isFinite(state?.removed_count) && (
          <div>
            {label('removed_count')}: {state.removed_count}
          </div>
        )}
        {Array.isArray(state?.managed_models) && (
          <div>
            {label('managed_models')}: {state.managed_models.length}
          </div>
        )}
        {missing.length > 0 && (
          <details>
            <summary>{label('pending_removals')}: {missing.length}</summary>
            {missing.map(([name]) => (
              <div key={name}>
                {name}
                {Number(state?.missing_since?.[name]) > 0 && (
                  <> - {label('removal_recheck_at')}: {modelSyncTimeV103(
                    Number(state.missing_since[name]) + 86400,
                    label('unknown')
                  )}</>
                )}
              </div>
            ))}
          </details>
        )}
        {state?.protected_models?.length > 0 && (
          <details>
            <summary>{label('protected_models')}: {state.protected_models.length}</summary>
            <div>{state.protected_models.join(', ')}</div>
          </details>
        )}
        {state?.message_code && (
          <div>
            {label('message_code')}:{' '}
            {messageCodes.includes(state.message_code)
              ? label(`messages.${state.message_code}`)
              : state.message_code}
          </div>
        )}
        {state?.alias_conflicts?.length > 0 && (
          <div>
            {label('alias_conflicts')}: {state.alias_conflicts.join(', ')}
          </div>
        )}
      </div>
      {error && <Message negative visible role='alert' content={error} />}
      {saved && (
        <Button
          type='button'
          size='small'
          onClick={onSync}
          disabled={
            disabled ||
            syncDisabled ||
            loading ||
            (state?.status === 'running' && !needsRefresh)
          }
          loading={loading}
          aria-label={action}
          title={syncDisabled ? label('enable_saved_sync') : label('sync_help')}
        >
          <Icon name='refresh' />
          {action}
        </Button>
      )}
    </section>
  );
}
